'use client';

import {trackShare} from '@/lib/analytics';
import {
  StyledTopicPageShareButton,
  StyledTopicPageSocialLinksContainer,
} from '@/components/pages/topic-page/TopicPageSocialLinks.styles';
import LinkedInIcon from '@/components/ui/icons/LinkedIn';
import FacebookIcon from '@/components/ui/icons/Facebook';
import XIcon from '@/components/ui/icons/X';
import EnvelopeIcon from '@/components/ui/icons/Envelope';
import CopyLinkIcon from '@/components/ui/icons/CopyLink';

export const TopicPageSocialLinks = () => {
  const openCenteredPopup = (url: string, name: string, width = 600, height = 600) => {
    const left = Math.max(0, Math.round((window.screen.width - width) / 2));
    const top = Math.max(0, Math.round((window.screen.height - height) / 2));
    return window.open(
      url,
      name,
      `popup=yes,width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
    );
  };

  const handleLinkedInShare = () => {
    trackShare({platform: 'linkedin'});
    const urlRaw = window.location.href;
    const url = encodeURIComponent(urlRaw);

    // LinkedIn's modern endpoint reliably pre-fills the URL.
    const shareOffsite = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;

    // Legacy endpoint sometimes pre-fills the post composer with title/summary,
    // but LinkedIn's behavior can vary.
    const title = encodeURIComponent(document.title || '');

    // Try the composer endpoint first; if blocked/redirected, user still gets the offsite share.
    const shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`;

    const win = openCenteredPopup(shareUrl, 'linkedin-share', 600, 600);

    // If the popup was blocked, fall back to opening a tab with the reliable endpoint.
    if (!win) {
      window.open(shareOffsite, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFacebookShare = () => {
    trackShare({platform: 'facebook'});
    const url = encodeURIComponent(window.location.href);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    const win = openCenteredPopup(shareUrl, 'facebook-share', 600, 650);

    // If popup blocked, fall back to opening a tab.
    if (!win) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleXShare = () => {
    trackShare({platform: 'x'});
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(document.title || '');
    const shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
    const win = openCenteredPopup(shareUrl, 'x-share', 600, 520);

    if (!win) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyLink = async () => {
    trackShare({platform: 'copy_link'});
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEmail = () => {
    trackShare({platform: 'email'});
    const url = window.location.href;
    const subject = encodeURIComponent('Jag vill dela en länk med dig');
    const body = encodeURIComponent(`${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <StyledTopicPageSocialLinksContainer>
      <StyledTopicPageShareButton
        title='Share on LinkedIn'
        aria-label='Share on LinkedIn'
        onClick={handleLinkedInShare}
      >
        <LinkedInIcon />
      </StyledTopicPageShareButton>
      <StyledTopicPageShareButton
        title='Share on Facebook'
        aria-label='Share on Facebook'
        onClick={handleFacebookShare}
      >
        <FacebookIcon />
      </StyledTopicPageShareButton>
      <StyledTopicPageShareButton
        title='Share on X'
        aria-label='Share on X'
        onClick={handleXShare}
      >
        <XIcon />
      </StyledTopicPageShareButton>
      <StyledTopicPageShareButton
        title='Email'
        aria-label='Email'
        onClick={handleEmail}
      >
        <EnvelopeIcon />
      </StyledTopicPageShareButton>
      <StyledTopicPageShareButton
        title='Copy link'
        aria-label='Copy link'
        onClick={handleCopyLink}
      >
        <CopyLinkIcon />
      </StyledTopicPageShareButton>
    </StyledTopicPageSocialLinksContainer>
  );
};
